import React, { useState, useEffect } from 'react';
import { useJobs } from '../services/JobContext';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import JobDetails from './JobDetails';
import RoleGuard from './RoleGuard';
import ConfirmDialog from './ConfirmDialog';
import { PERMISSIONS, ROLES } from '../utils/permissions';
import { useFilters } from '../hooks/useFilters';
import { filterJobs } from '../utils/helpers';
import { JOB_STATUSES } from '../constants';
import api from '../services/api';
import { SkeletonTable } from './SkeletonLoader';

const JOB_TYPE_LABELS = {
  'W-2': 'W-2',
  C2C: 'C2C',
  '1099': '10-99',
  // Legacy/enum-style values mapped to display labels
  FULL_TIME: 'W-2',
  CONTRACTOR_1099: '10-99',
};

const JobList = () => {
  const { jobs, deleteJob, getJobInterviews, loading } = useJobs();
  const navigate = useNavigate();
  const { filters, updateFilter, clearFilters } = useFilters();
  const [selectedJob, setSelectedJob] = useState(null);
  const [bdOptions, setBdOptions] = useState([]);
  const [loadingBds, setLoadingBds] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    const loadBds = async () => {
      setLoadingBds(true);
      try {
        const users = await api.getUsers();
        const bds = (users || [])
          .filter((u) => u.role === ROLES.BD)
          .map((u) => u.name || u.email)
          .filter(Boolean);

        const uniqueBds = Array.from(new Set(bds));
        setBdOptions(uniqueBds);
      } catch (error) {
        console.error('Failed to load BD users for filters', error);
        setBdOptions([]);
      } finally {
        setLoadingBds(false);
      }
    };

    loadBds();
  }, []);

  const filteredJobs = filterJobs(jobs, {
    searchTerm: filters.searchTerm,
    bdName: filters.bdName,
    status: filters.status
  });

  const handleDeleteJob = (jobId) => {
    setPendingDeleteId(jobId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteJob = () => {
    if (pendingDeleteId) {
      deleteJob(pendingDeleteId);
    }
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };

  const cancelDeleteJob = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };



  return (
    <div>
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={confirmDeleteJob}
        onCancel={cancelDeleteJob}
        title="Delete Job"
        message="Are you sure you want to delete this job? This action cannot be undone."
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
        confirmDanger={true}
      />

      <div className="page-header">
        <div className="page-header-text">
          <h1>Jobs</h1>
          <p>Browse and manage all open jobs across your team.</p>
        </div>
        <div className="page-header-actions">
          <RoleGuard permission={PERMISSIONS.CREATE_JOB} fallback={null}>
            <button className="btn btn-primary" onClick={() => navigate('/add-job')}>
              Add Job
            </button>
          </RoleGuard>
        </div>
      </div>

      <div className="card">
        <div className="filters job-list-filters">
          <div className="form-group">
            <label>Search</label>
            <input
              type="text"
              name="searchTerm"
              value={filters.searchTerm}
              onChange={(e) => updateFilter('searchTerm', e.target.value)}
              placeholder="Search company or position…"
            />
          </div>

          <div className="form-group">
            <label>BD Name</label>
            <select
              name="bdName"
              value={filters.bdName}
              onChange={(e) => updateFilter('bdName', e.target.value)}
              disabled={loadingBds}
            >
              <option value="">{loadingBds ? 'Loading…' : 'All BDs'}</option>
              {bdOptions.map((bd) => (
                <option key={bd} value={bd}>{bd}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              {Object.entries(JOB_STATUSES).map(([key, value]) => (
                <option key={key} value={value}>{key.charAt(0) + key.slice(1).toLowerCase()}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>&nbsp;</label>
            <button
              className="btn btn-secondary"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>All Jobs</h3>
          <span className="badge badge-neutral">{filteredJobs.length} total</span>
        </div>
        {loading ? (
          <SkeletonTable rows={5} columns={9} />
        ) : filteredJobs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No jobs found</div>
            <div className="empty-state-desc">Try adjusting your filters or add a new job.</div>
          </div>
        ) : (
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Company</th>
                <th>Position</th>
                <th>BD Name</th>
                <th>Job Type</th>
                <th>Pay Range</th>
                <th>Status</th>
                <th>Interviews</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(job => {
                const jobInterviews = getJobInterviews(job.id);
                return (
                  <tr key={job.id}>
                    <td>{job.companyName}</td>
                    <td>{job.position}</td>
                    <td>{job.bdName}</td>
                    <td className="col-short">{JOB_TYPE_LABELS[job.jobType] || job.jobType}</td>
                    <td className="col-short">{job.payRange || 'N/A'}</td>
                    <td className="col-status">
                      <span className={`status-badge status-${job.status}`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="col-num">{jobInterviews.length}</td>
                    <td className="col-date">{format(new Date(job.createdAt), 'MMM dd, yyyy')}</td>
                    <td className="col-actions">
                      <div className="job-list-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedJob(job)}
                        >
                          View
                        </button>
                        <RoleGuard permission={PERMISSIONS.EDIT_JOB} fallback={null}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate(`/jobs/edit/${job.id}`)}
                          >
                            Edit
                          </button>
                        </RoleGuard>
                        <RoleGuard permission={PERMISSIONS.SCHEDULE_INTERVIEW} fallback={null}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => navigate(`/schedule?jobId=${job.id}`)}
                          >
                            Schedule
                          </button>
                        </RoleGuard>
                        <RoleGuard permission={PERMISSIONS.DELETE_JOB} fallback={null}>
                          <button
                            className="btn btn-danger-outline btn-sm"
                            onClick={() => handleDeleteJob(job.id)}
                          >
                            Delete
                          </button>
                        </RoleGuard>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedJob && (
        <JobDetails 
          job={selectedJob} 
          onClose={() => setSelectedJob(null)} 
        />
      )}
      
    </div>
  );
};

export default JobList;